import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styles from './AthleteProfile.module.css';
import routes from 'routes';
import PinProtectedButton from 'components/common/PinProtected/PinProtectedButton';
import { formatExpiresRelative } from 'utils/time';
import { clubForId } from 'utils/clubFeatures';
import { groupingBasisOf, groupWords, formatGroupCell } from 'utils/groupLabel';
import { teamById } from 'utils/teamLabel';
import TeamColourDot from 'components/admin/TeamColourDot';

// What the card is given when the page has no team list for it: a club without teams.
const NO_CLUB_TEAMS = { hasTeams: false, teams: [], teamWords: null };

// Renders ALL of this member's athletes (where they're the primary contact),
// split into two groups: those currently linked to this club (full per-club info,
// existing controls) and those not currently linked (profile info + which other
// clubs they're in, plus an "Add to this club" affordance). The athlete profile
// itself is platform-wide; only the AthleteClubMembership for this club is
// created/removed via the actions here. See docs/role-and-deletion-design.html §3.

const AthleteProfiles = ({
  athletes,
  otherClubAthletes,
  clubId,
  email,
  onEditAthlete,
  onAddAthlete,
  onDeleteAthlete,
  onLinkAthlete,
  canEdit = true,
  canAdd = true,
  clubTeams = NO_CLUB_TEAMS,
}) => {
  // A club that has teams (a school's houses or factions) shows each athlete's team, in a column headed with
  // the club's own word. The page loads the club's teams once and passes them in.
  const { hasTeams, teams, teamWords } = clubTeams;
  const hasInClub = athletes && athletes.length > 0;
  const hasOther = otherClubAthletes && otherClubAthletes.length > 0;

  // The column that holds an athlete's group is headed Age and shows the group number for an age club, and is
  // headed Year and shows the year (Y5) for a club that groups by year level. See utils/groupLabel.js.
  const groupingBasis = useSelector((state) => groupingBasisOf(clubForId(state.user, clubId)));

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Athlete Profiles</h3>
        {canAdd && (
          <button
            className={`${styles.editButton} ${styles.addButton}`}
            onClick={onAddAthlete}
            title="Add New Athlete Profile"
          >
            ➕
          </button>
        )}
      </div>

      <div className={styles.cardContent}>
        {hasInClub && (
          <>
            {hasOther && (
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#475569' }}>
                In this club
              </h4>
            )}
            <table className={styles.athleteTable}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Gender</th>
                  <th>{groupWords(groupingBasis).value}</th>
                  {hasTeams && <th>{teamWords.team}</th>}
                  <th>Active</th>
                  <th>Membership</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {athletes.map((athlete, index) => (
                  <tr key={athlete.athleteID || index}>
                    <td>{athlete.clubAthleteNumber || 0}</td>
                    <td>
                      {athlete.firstName} {athlete.lastName}
                    </td>
                    <td>{athlete.gender}</td>
                    <td>{formatGroupCell(groupingBasis, athlete.ageGroup)}</td>
                    {hasTeams && (
                      <td>
                        {teamById(teams, athlete.teamID) && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <TeamColourDot colour={teamById(teams, athlete.teamID).colour} />
                            {teamById(teams, athlete.teamID).name}
                          </span>
                        )}
                      </td>
                    )}
                    <td>{athlete.participationStatus}</td>
                    <td>{athlete.membershipType}</td>
                    <td>
                      {/* Guest-class memberships (Guest + Visitor) carry a meet/season expiry window; Full members
                          never expire. Keep this in step with the server's MembershipTypes.IsGuestClass. */}
                      {athlete.membershipType === 'Guest' ||
                      athlete.membershipType === 'Visitor'
                        ? formatExpiresRelative(athlete.expiresAt)
                        : 'No'}
                    </td>
                    <td>
                      <div className={styles.athleteActions}>
                        <Link
                          to={routes.admin.members.athleteResults.url(
                            clubId,
                            athlete.athleteID
                          )}
                          className={styles.actionButton}
                          title="View Athlete Results"
                        >
                          👁️
                        </Link>

                        {canEdit && (
                          <>
                            <button
                              onClick={() => onEditAthlete(athlete)}
                              className={styles.actionButton}
                              title="Edit Athlete"
                            >
                              ✏️
                            </button>
                            <PinProtectedButton
                              clubId={clubId}
                              actionType="delete-athlete"
                              description="The Admin PIN is required to remove an athlete from the club. The athlete profile and historical race results are preserved, along with their PRs (which are cleaned up at the next season start if the athlete doesn't rejoin). Their meet registrations and points for this club will be removed."
                              onAction={(athleteData, pin) =>
                                onDeleteAthlete(athleteData)
                              }
                              actionData={athlete}
                            >
                              <button
                                className={`${styles.actionButton} ${styles.deleteAction}`}
                                title="Remove Athlete from Club"
                              >
                                🗑️
                              </button>
                            </PinProtectedButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {hasOther && (
          <>
            <h4
              style={{
                margin: `${hasInClub ? '1.5rem' : '0'} 0 0.5rem 0`,
                fontSize: '0.875rem',
                color: '#475569',
              }}
            >
              Not in this club
            </h4>
            <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 0.75rem 0' }}>
              These athlete profiles belong to this member but are not currently
              members of your club. Use Add to bring any of them in.
            </p>
            <table className={styles.athleteTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Gender</th>
                  <th>Date of Birth</th>
                  <th>Currently In</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {otherClubAthletes.map((athlete, index) => (
                  <tr key={athlete.athleteID || `other-${index}`}>
                    <td>
                      {athlete.firstName} {athlete.lastName}
                    </td>
                    <td>{athlete.gender}</td>
                    <td>{athlete.birthDate || ''}</td>
                    <td>
                      {athlete.currentClubs && athlete.currentClubs.length > 0
                        ? athlete.currentClubs.join(', ')
                        : <span style={{ color: '#94a3b8' }}>No clubs</span>}
                    </td>
                    <td>
                      <div className={styles.athleteActions}>
                        {canEdit && (
                          <button
                            onClick={() => onLinkAthlete(athlete)}
                            className={styles.actionButton}
                            title="Add to This Club"
                          >
                            ➕
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {!hasInClub && !hasOther && (
          <div className={styles.emptyState}>
            <p>No athlete profiles added</p>
          </div>
        )}
      </div>
    </div>
  );
};

AthleteProfiles.propTypes = {
  athletes: PropTypes.arrayOf(PropTypes.object),
  otherClubAthletes: PropTypes.arrayOf(PropTypes.object),
  clubId: PropTypes.string,
  email: PropTypes.string,
  onEditAthlete: PropTypes.func.isRequired,
  onAddAthlete: PropTypes.func.isRequired,
  onDeleteAthlete: PropTypes.func.isRequired,
  onLinkAthlete: PropTypes.func,
  canEdit: PropTypes.bool,
  canAdd: PropTypes.bool,
  // The club's teams, from useClubTeams: { hasTeams, teams, teamWords }. Left out for a club without teams.
  clubTeams: PropTypes.shape({
    hasTeams: PropTypes.bool,
    teams: PropTypes.array,
    teamWords: PropTypes.object,
  }),
};

export default AthleteProfiles;
